import { Workbook } from 'exceljs';
import { fetchAdminProgressStatsRows } from '../../service/form';
import type { FormListItem } from '../../service/types';
import { downloadExcelBuffer } from '../../utils/list-page-excel/workbook';

export type CompletionReportForm = Pick<
  FormListItem,
  'formId' | 'formState' | 'statusEnteredAt' | 'approvedAt'
> & { currentStatus: string; activeQuarter: string };

const text = (value: unknown) => (value == null ? '' : String(value));

const completionDate = (value: unknown): Date | null => {
  if (!value) return null;
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(text(value));
  if (match) {
    const [, day, month, year] = match.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    )
      return date;
  }
  throw new Error('Invalid completion date. Unable to download report.');
};

const stateDate = (form: CompletionReportForm): Date | null => {
  const candidates = [form.statusEnteredAt];
  if (['approved', 'submitted'].includes(text(form.formState).trim().toLowerCase())) {
    candidates.push(form.approvedAt);
  }
  for (const value of candidates) {
    if (!value) continue;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) continue;
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).formatToParts(date);
    const part = (name: string) => parts.find((item) => item.type === name)?.value;
    return completionDate(`${part('day')}-${part('month')}-${part('year')}`);
  }
  return null;
};

export const buildScsCompletionWorkbook = (
  rows: Array<Record<string, unknown>>,
  forms: CompletionReportForm[],
  year: string,
) => {
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('Completion Report');
  sheet.properties.defaultRowHeight = 15;
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.addRow([
    'Business Unit',
    'SCS name',
    'SCS Email',
    'Approver email',
    'Current status',
    'State Date',
    ...[1, 2, 3, 4].flatMap((quarter) => [`Q${quarter} Staff`, `Q${quarter} Manager`]),
  ]);
  const selected = forms.map((form) => {
    const matches = rows.filter(
      (row) => row.form_id === form.formId && row.tenant === 'scs' && row.year === year,
    );
    if (matches.length !== 1)
      throw new Error(
        'Completion data is missing or has changed. Refresh the admin page and try again.',
      );
    return { form, row: matches[0] };
  });
  selected.sort((a, b) => text(a.row.staff_member).localeCompare(text(b.row.staff_member)));
  for (const { form, row } of selected) {
    sheet.addRow([
      text(row.business_unit),
      text(row.staff_member),
      text(row.staff_email),
      text(row.line_manager_email),
      text(row.status),
      stateDate(form),
      ...[1, 2, 3, 4].flatMap((quarter) => [
        completionDate(row[`q${quarter}_staff_complete`]),
        completionDate(row[`q${quarter}_manager_complete`]),
      ]),
    ]);
  }
  sheet.columns.forEach((column, index) => {
    column.width = index < 5 ? 35 : 18;
    if (index >= 5) column.numFmt = 'dd/mm/yyyy';
  });
  sheet.eachRow((row, index) => {
    row.height = 15;
    row.font = { name: 'Calibri', size: 11, bold: index === 1 };
  });
  return workbook;
};

export const downloadScsCompletionReport = async (
  token: string,
  formIds: string[],
  forms: CompletionReportForm[],
  year: string,
) => {
  const ids = [...new Set(formIds)];
  const selected = ids.map((id) => {
    const form = forms.find((item) => item.formId === id);
    if (!form) throw new Error('Response unavailable. Refresh the admin page and try again.');
    return form;
  });
  const rows = await fetchAdminProgressStatsRows(token, { reportFilters: [year], formIds: ids });
  const buffer = await buildScsCompletionWorkbook(rows, selected, year).xlsx.writeBuffer();
  const now = new Date();
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  downloadExcelBuffer(buffer as ArrayBuffer, `${date}_completion_report.xlsx`);
};
